import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/components/state/store";
import { canEditUserRolesOnAdmin } from "@/helper/userRolePermissions";
import { Department } from "@/components/userAdministration/administration";
import "@/components/userAdministration/userAdministration.css";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import { KAER_COMPANY_ID } from "@/helper/systemIds";
import RoleSelect from "@/components/userAdministration/roleSelect";
import { userEntityAdapter } from "@/components/userAdministration/userEntityAdapter";
import type { BulkActionsSlotContext } from "@/components/tables/toolbar/bulkActionsSlot";
import {
  PORTAL_REGISTRATION_EDIT_COLUMNS,
  useUserTableColumns,
} from "@/components/userAdministration/userTable/useUserTableColumns";
import { useTwoFactorToggle } from "@/components/userAdministration/userTable/useTwoFactorToggle";
import { userRowActions } from "@/components/userAdministration/userTable/userRowActions";
import { userBulkActions } from "@/components/userAdministration/userTable/userBulkActions";
import { useUserBulkActions } from "@/components/userAdministration/userTable/useUserBulkActions";
import { useUserMailLogDialog } from "@/components/userAdministration/userTable/dialogs/useUserMailLogDialog";
import UserMailLogDialog from "@/components/userAdministration/userTable/dialogs/userMailLogDialog";
import MoveUserDialog, {
  type MoveTarget,
} from "@/components/userAdministration/userTable/dialogs/moveUserDialog";
import BulkDepartmentDialog from "@/components/userAdministration/userTable/dialogs/bulkDepartmentDialog";
import MailPreferencesDialog from "@/components/mailTemplate/mailPreferencesDialog";

interface Props {
  companySubjectId?: string;
  uploadSuccessful?: boolean;
  handleSendRegistrationEmail?: (
    row: any,
    skipQuestion?: false | undefined
  ) => void;
  companyId?: string;
  department?: Department[];
  handleUnblockUser?: (row: any, skipQuestion?: false | undefined) => void;
  handleUndeleteUser?: (
    row: any,
    reset: () => void,
    skipQuestion?: false | undefined
  ) => void;
  handleUploadSuccess?: () => void;
  triggerCompanyManagerTableReload?: () => void;
  triggerReload?: boolean;
  customCreateHandler?: any;
}

export default function UserTable(props: Props) {
  const { t } = useTranslation();
  const subdomain = window.location.hostname.split(".")[0];
  const permissions = useSelector((state: RootState) => state.login.permissions);
  const canEditUserRoles = canEditUserRolesOnAdmin(subdomain, permissions);
  const { handleError, errorModal } = useErrorModal();

  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const notify = (
    message: string,
    variant: "success" | "error" | "warning" | "info"
  ) => {
    setNotificationMessage(message);
    setNotificationVariant(variant);
    setShowNotification(true);
  };
  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [mailPreferenceSubject, setMailPreferenceSubject] = useState<string | null>(
    null
  );
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);
  const [usersForDepartments, setUsersForDepartments] = useState<any[]>([]);
  const [bulkAddToDepartmentModalOpen, setBulkAddToDepartmentModalOpen] =
    useState(false);

  const { deletePeople, undeletePeople, sendRegistrationMails } =
    useUserBulkActions({
      companySubjectId: props.companySubjectId,
      refreshTableAfterAction: () => props.triggerCompanyManagerTableReload?.(),
      notify,
      onError: handleError,
    });
  const mailLog = useUserMailLogDialog(handleError);
  const toggleTwoFactor = useTwoFactorToggle({
    notify,
    onError: handleError,
    onSaved: () => setRefreshTable((prev) => !prev),
  });
  const userColumns = useUserTableColumns({
    companyId: props.companyId,
    companySubjectId: props.companySubjectId,
    subdomain,
    canEditUserRoles,
    onToggleTwoFactor: toggleTwoFactor,
  });

  const openMoveDialogForUser = (row: any, clearTableSelection: () => void) =>
    setMoveTarget({ kind: "singleUser", user: row.original, clearTableSelection });
  const openMoveDialogForUsers = (
    users: any[],
    table: BulkActionsSlotContext
  ) =>
    setMoveTarget({
      kind: "selectedUsers",
      users,
      clearTableSelection: table.clearSelection,
    });
  const openBulkDepartmentDialog = (users: any[]) => {
    setUsersForDepartments(users);
    setBulkAddToDepartmentModalOpen(true);
  };

  useEffect(() => {
    if (props.triggerReload === undefined) return;
    setRefreshTable((prev) => !prev);
  }, [props.triggerReload]);

  const additionalUrlParameter = useMemo(
    () => ({
      departments: true,
      include_relations: true,
      include_deleted: subdomain === "admin" ? true : false,
      ...(props.department?.length
        ? { department_id: props.department[0].departmentId }
        : {}),
    }),
    [subdomain, props.department]
  );

  return (
    <>
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        enableRowSelection={true}
        entity={"user"}
        entityAdapter={userEntityAdapter}
        columns={userColumns}
        additionalColumnsForEdit={PORTAL_REGISTRATION_EDIT_COLUMNS}
        dropFiltersForRemovedColumns={true}
        companySubjectId={props.companySubjectId}
        companyId={props.companyId}
        uploadSuccessful={props.uploadSuccessful}
        additionalUrlParameter={additionalUrlParameter}
        rowActions={userRowActions({
          t,
          inCompanyContext: Boolean(props.companySubjectId),
          sendRegistrationEmail: props.handleSendRegistrationEmail,
          unblock: props.handleUnblockUser,
          undelete: props.handleUndeleteUser,
          showMails: mailLog.showMailsSentToUser,
          move: openMoveDialogForUser,
          mailPreferences:
            subdomain === "admin" && permissions.includes("change_mailtemplate")
              ? (row) => setMailPreferenceSubject(row.original.subject_id)
              : undefined,
        })}
        customRowActionsLayout="menu"
        bulkActions={userBulkActions({
          t,
          subdomain,
          deletePeople,
          undeletePeople,
          sendRegistrationMails,
          addToDepartments: openBulkDepartmentDialog,
          moveToCompany: openMoveDialogForUsers,
        })}
        refreshTable={refreshTable}
        customColumnVisibility={{ created_on: false }}
        refreshTableAfterAction={() => {
          props.triggerCompanyManagerTableReload?.();
        }}
        customCreateHandler={props.customCreateHandler}
        additionalComponentForModal={
          props.companyId !== KAER_COMPANY_ID && canEditUserRoles
            ? ({ modalMode, presetValues }) => (
                <RoleSelect
                  companyId={
                    modalMode === "edit"
                      ? (presetValues?.company_id ?? props.companyId)
                      : props.companyId
                  }
                  effectiveRolesOfEditedUser={
                    modalMode === "edit" ? (presetValues?.group_set ?? []) : []
                  }
                  directRolesOfEditedUser={
                    modalMode === "edit"
                      ? (presetValues?.direct_group_set ?? [])
                      : []
                  }
                />
              )
            : undefined
        }
      />
      <UserMailLogDialog {...mailLog.dialogProps} />
      <MoveUserDialog
        target={moveTarget}
        onClose={() => setMoveTarget(null)}
        subdomain={subdomain}
        notify={notify}
        onError={handleError}
        onMoved={props.handleUploadSuccess}
      />
      <BulkDepartmentDialog
        open={bulkAddToDepartmentModalOpen}
        onOpenChange={setBulkAddToDepartmentModalOpen}
        companyId={props.companyId}
        companySubjectId={props.companySubjectId}
        selectedUsers={usersForDepartments}
        notify={notify}
        onError={handleError}
        onAdded={props.handleUploadSuccess}
      />
      {errorModal}
      {mailPreferenceSubject && (
        <MailPreferencesDialog
          key={mailPreferenceSubject}
          subjectId={mailPreferenceSubject}
          onClose={() => setMailPreferenceSubject(null)}
        />
      )}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
}
